import { ARRAY_SIZE_OF_ONE_ELEMENT, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    ICashFlowOperatingActivities,
    ICashFlowStatementEntry,
    ICashFlowStatementInvestingEntry,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemCashFlowType, FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { EMPTY_STRING } from '../../../../aumico-lib-settings-fe/src/lib/constants/constants';
import { ICashFlowFLI, ICashFlowInvestingFLI } from '../interfaces/ICashFlowFLI.interface';
import { ICashFlowFinancingActivitiesComponentInput } from '../interfaces/ICashFlowFinancingActivitiesComponentInput.interface';
import { ICashFlowInvestingActivitiesComponentInput } from '../interfaces/ICashFlowInvestingActivitiesComponentInput.interface';
import { ICashFlowOperatingActivitiesComponentInput } from '../interfaces/ICashFlowOperatingActivitiesComponentInput.interface';
export class CashFlowStatementHelper {
    public static prepareTableEntriesForOperatingActivities(
        operatingActivitiesFlis: ICashFlowFLI[],
        isAutoFillApplied: boolean,
    ): ICashFlowStatementEntry[] {
        let tableEntries: ICashFlowStatementEntry[] = [];
        const operationsEntries: ICashFlowStatementEntry[] = [];
        const netWorkingEntries: ICashFlowStatementEntry[] = [];

        operatingActivitiesFlis.forEach((fli: ICashFlowFLI) => {
            let previousValue: number | null = null;
            if (fli.previousValue != null && NumberUtils.isFinite(fli.previousValue)) {
                previousValue = NumberUtils.roundNumber(fli.previousValue);
            }
            if (fli.fliCashFlow === FinancialLineItemCashFlowType.OPERATIONS) {
                if (fli.isCustom) {
                    let currentValue: number | null = null;
                    if (fli.currentValue != null && NumberUtils.isFinite(fli.currentValue)) {
                        currentValue = NumberUtils.roundNumber(fli.currentValue);
                    }

                    const entryToPush: ICashFlowStatementEntry = {
                        name: fli.caption,
                        calculatedCurrentValue: currentValue,
                        calculatedPreviousValue: previousValue,
                        fliId: null,
                        isCustom: true,
                    };
                    tableEntries.push(entryToPush);
                    operationsEntries.push(entryToPush);
                } else {
                    const filteredNumbersToBeMultiplied = NumberUtils.filterNonNumber([fli.currentValue, -1]);
                    let currentValue: number | null = null;
                    if (filteredNumbersToBeMultiplied.length > ARRAY_SIZE_OF_ONE_ELEMENT) {
                        currentValue = NumberUtils.multiplyNumbers(filteredNumbersToBeMultiplied);
                    }

                    const entryToPush: ICashFlowStatementEntry = {
                        name: fli.caption,
                        calculatedCurrentValue: currentValue,
                        calculatedPreviousValue: previousValue,
                        fliId: fli.id,
                        type: fli.fliCashFlow,
                    };
                    tableEntries.push(entryToPush);
                    operationsEntries.push(entryToPush);
                }
            } else if (fli.fliCashFlow === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
                const entryToPush: ICashFlowStatementEntry = {
                    name: this.getCaptionOfNetworkCapital(fli.caption),
                    calculatedCurrentValue: this.calculateCurrentNetworkingCapitalValue(fli),
                    calculatedPreviousValue: previousValue,
                    fliId: fli.id,
                    type: fli.fliCashFlow,
                };
                tableEntries.push(entryToPush);
                netWorkingEntries.push(entryToPush);
            } else if (fli.isCustom) {
                const entryToPush: ICashFlowStatementEntry = {
                    name: fli.caption,
                    calculatedCurrentValue: fli.currentValue,
                    calculatedPreviousValue: fli.previousValue,
                    fliId: null,
                    isCustom: true,
                };
                tableEntries.push(entryToPush);
            }
        });
        if (!isAutoFillApplied) {
            tableEntries = [];
            // we reOrder entries to have operating entries first then netWorking capital
            tableEntries.push(...operationsEntries);
            tableEntries.push(...netWorkingEntries);
        }

        return tableEntries;
    }

    private static getCaptionOfNetworkCapital(caption: string): string {
        if (caption?.includes('Δ')) {
            return caption;
        }
        return `Δ ${caption ?? EMPTY_STRING}`;
    }
    public static prepareTableEntriesForInvestingActivities(
        investingActivitiesFlis: ICashFlowInvestingFLI[],
        investingKeyValue: string,
        disinvestingKeyValue: string,
    ): ICashFlowStatementInvestingEntry[] {
        const tableEntries: ICashFlowStatementInvestingEntry[] = [];
        const parentToChildrenMap = new Map<string, ICashFlowInvestingFLI[]>();

        investingActivitiesFlis.forEach((fli: ICashFlowInvestingFLI) => {
            if (fli.parentFli) {
                if (parentToChildrenMap.has(fli.parentFli)) {
                    parentToChildrenMap.get(fli.parentFli)!.push(fli);
                } else {
                    parentToChildrenMap.set(fli.parentFli, [fli]);
                }
            }
        });
        investingActivitiesFlis.forEach((fli: ICashFlowInvestingFLI) => {
            if (!fli.parentFli) {
                const fliId = fli.id ?? fli.caption;
                tableEntries.push({
                    fliId,
                    name: fli.caption,
                    calculatedCurrentValue: null,
                    calculatedPreviousValue: null,
                    staticLabel: true,
                    labelEditMode: false,
                    parentFli: null,
                    investing: false,
                    disinvesting: false,
                    isCustom: fli.isCustom,
                });

                const children = parentToChildrenMap.get(fli.id);
                if (children?.length) {
                    children.forEach((child, index) => {
                        let currentValue: number | null = null;
                        if (child.currentValue != null && NumberUtils.isFinite(child.currentValue)) {
                            currentValue = NumberUtils.roundNumber(child.currentValue);
                        }

                        let previousValue: number | null = null;
                        if (child.previousValue != null && NumberUtils.isFinite(child.previousValue)) {
                            previousValue = NumberUtils.roundNumber(child.previousValue);
                        }

                        if (index === 0) {
                            tableEntries.push({
                                fliId: null,
                                name: child.caption,
                                calculatedCurrentValue: currentValue,
                                calculatedPreviousValue: previousValue,
                                staticLabel: true,
                                labelEditMode: false,
                                parentFli: fliId,
                                investing: true,
                                disinvesting: false,
                                isCustom: fli.isCustom,
                            });
                        } else if (index === 1) {
                            tableEntries.push({
                                fliId: null,
                                name: child.caption,
                                calculatedCurrentValue: currentValue,
                                calculatedPreviousValue: previousValue,
                                staticLabel: true,
                                labelEditMode: false,
                                parentFli: fliId,
                                investing: false,
                                disinvesting: true,
                                isCustom: fli.isCustom,
                            });
                        } else {
                            // Artibtrary value for the investing/disinvesting columns for custom rows
                            tableEntries.push({
                                fliId: null,
                                name: child.caption,
                                calculatedCurrentValue: currentValue,
                                calculatedPreviousValue: previousValue,
                                staticLabel: true,
                                labelEditMode: false,
                                parentFli: fliId,
                                investing: true,
                                disinvesting: false,
                                isCustom: fli.isCustom,
                            });
                        }
                    });
                } else {
                    tableEntries.push({
                        fliId: null,
                        name: investingKeyValue,
                        calculatedCurrentValue: null,
                        calculatedPreviousValue: null,
                        staticLabel: true,
                        labelEditMode: false,
                        parentFli: fliId,
                        investing: true,
                        disinvesting: false,
                        isCustom: fli.isCustom,
                    });
                    tableEntries.push({
                        fliId: null,
                        name: disinvestingKeyValue,
                        calculatedCurrentValue: null,
                        calculatedPreviousValue: null,
                        staticLabel: true,
                        labelEditMode: false,
                        parentFli: fliId,
                        investing: false,
                        disinvesting: true,
                        isCustom: fli.isCustom,
                    });
                }
            }
        });

        return tableEntries;
    }

    public static prepareTableEntriesForFinancingActivities(
        financingActivitiesFlis: ICashFlowFLI[],
    ): ICashFlowStatementEntry[] {
        const tableEntries: ICashFlowStatementEntry[] = [];
        financingActivitiesFlis.forEach((fli: ICashFlowFLI) => {
            let currentValue: number | null = null;
            if (fli.currentValue != null && NumberUtils.isFinite(fli.currentValue)) {
                currentValue = NumberUtils.roundNumber(fli.currentValue);
            }

            let previousValue: number | null = null;
            if (fli.previousValue != null && NumberUtils.isFinite(fli.previousValue)) {
                previousValue = NumberUtils.roundNumber(fli.previousValue);
            }
            if (fli.isCustom) {
                tableEntries.push({
                    fliId: null,
                    name: fli.caption,
                    calculatedCurrentValue: currentValue,
                    calculatedPreviousValue: previousValue,
                    isCustom: true,
                });
            } else {
                let calculatedCurrentValue: number | null = null;
                if (
                    (fli.currentValue != null && NumberUtils.isFinite(fli.currentValue)) ||
                    (fli.openingValue != null && NumberUtils.isFinite(fli.openingValue))
                ) {
                    calculatedCurrentValue = NumberUtils.subtractNumbers(fli.currentValue ?? 0, fli.openingValue ?? 0);
                }
                tableEntries.push({
                    fliId: fli.id,
                    name: fli.caption,
                    calculatedCurrentValue,
                    calculatedPreviousValue: previousValue,
                    type: fli.fliCashFlow,
                });
            }
        });
        return tableEntries;
    }

    public static updateFinancingActivitiesFlisValues(
        formValue: ICashFlowStatementEntry[],
        financingActivitiesData: ICashFlowFinancingActivitiesComponentInput,
    ): ICashFlowStatementEntry[] {
        if (!formValue) {
            return [];
        }

        const financingActivitiesFlisMap = this.createFlisMap(financingActivitiesData?.flis ?? []);
        const updatedFormValue: ICashFlowStatementEntry[] = [];
        const existingFliIdsInForm = new Set<string>();

        // Update existing entries and filter out removed ones
        for (const entry of formValue) {
            const updatedEntry = this.updateFinancingEntry(entry, financingActivitiesFlisMap);
            if (updatedEntry) {
                updatedFormValue.push(updatedEntry);
                if (updatedEntry.fliId) {
                    existingFliIdsInForm.add(updatedEntry.fliId);
                }
            }
        }

        // Identify and add new FLIs that are not already in the form
        const newFlis = (financingActivitiesData?.flis ?? []).filter((fli) => !existingFliIdsInForm.has(fli.id));

        if (newFlis.length > 0) {
            const newFlisTableEntries = this.prepareTableEntriesForFinancingActivities(newFlis);
            updatedFormValue.push(...newFlisTableEntries);
        }

        return updatedFormValue;
    }

    private static updateFinancingEntry(
        entry: ICashFlowStatementEntry,
        financingActivitiesFlisMap: Map<string, ICashFlowFLI>,
    ): ICashFlowStatementEntry | null {
        const matchingFli = financingActivitiesFlisMap.get(entry.fliId);

        // Remove non-custom entries that are no longer in the mapping
        if (!matchingFli && entry.fliId && !entry.isCustom) {
            return null;
        }

        // Update values for existing, non-custom entries
        if (matchingFli && !entry.isCustom) {
            let calculatedCurrentValue: number | null = null;
            if (
                (matchingFli.currentValue != null && NumberUtils.isFinite(matchingFli.currentValue)) ||
                (matchingFli.openingValue != null && NumberUtils.isFinite(matchingFli.openingValue))
            ) {
                calculatedCurrentValue = NumberUtils.subtractNumbers(
                    matchingFli.currentValue ?? 0,
                    matchingFli.openingValue ?? 0,
                );
            }
            entry.calculatedCurrentValue = calculatedCurrentValue;
        }

        // Keep custom entries or updated entries
        return entry;
    }

    /**
     * Updates financing activities FLI values while preserving manual overrides (Shadow Copy Pattern).
     */
    public static updateFinancingActivitiesFlisValuesWithShadowCopy(
        formValue: ICashFlowStatementEntry[],
        financingActivitiesData: ICashFlowFinancingActivitiesComponentInput,
    ): ICashFlowStatementEntry[] {
        if (!formValue) {
            return [];
        }

        const financingActivitiesFlisMap = this.createFlisMap(financingActivitiesData?.flis ?? []);
        const updatedFormValue: ICashFlowStatementEntry[] = [];
        const existingFliIdsInForm = new Set<string>();

        // Update existing entries and filter out removed ones
        for (const entry of formValue) {
            const updatedEntry = this.updateFinancingEntryWithShadowCopy(entry, financingActivitiesFlisMap);
            if (updatedEntry) {
                updatedFormValue.push(updatedEntry);
                if (updatedEntry.fliId) {
                    existingFliIdsInForm.add(updatedEntry.fliId);
                }
            }
        }

        // Identify and add new FLIs that are not already in the form
        const newFlis = (financingActivitiesData?.flis ?? []).filter((fli) => !existingFliIdsInForm.has(fli.id));

        if (newFlis.length > 0) {
            const newFlisTableEntries = this.prepareTableEntriesForFinancingActivities(newFlis);
            updatedFormValue.push(...newFlisTableEntries);
        }

        return updatedFormValue;
    }

    /**
     * Update financing entry while preserving manual overrides.
     */
    private static updateFinancingEntryWithShadowCopy(
        entry: ICashFlowStatementEntry,
        financingActivitiesFlisMap: Map<string, ICashFlowFLI>,
    ): ICashFlowStatementEntry | null {
        const matchingFli = financingActivitiesFlisMap.get(entry.fliId);

        // Remove non-custom entries that are no longer in the mapping
        if (!matchingFli && entry.fliId && !entry.isCustom) {
            return null;
        }

        // Update system values for existing, non-custom entries
        if (matchingFli && !entry.isCustom) {
            let calculatedCurrentValue: number | null = null;
            if (
                (matchingFli.currentValue != null && NumberUtils.isFinite(matchingFli.currentValue)) ||
                (matchingFli.openingValue != null && NumberUtils.isFinite(matchingFli.openingValue))
            ) {
                calculatedCurrentValue = NumberUtils.subtractNumbers(
                    matchingFli.currentValue ?? 0,
                    matchingFli.openingValue ?? 0,
                );
            }
            // Only update the system value (calculatedCurrentValue)
            // Manual overrides (manuallyEnteredCurrentValue/manuallyEnteredPreviousValue) are preserved as-is
            entry.calculatedCurrentValue = calculatedCurrentValue;
        }

        return entry;
    }

    public static updateInvestingActivitiesFlisValues(
        formValue: { children: ICashFlowStatementInvestingEntry[]; parent: ICashFlowStatementInvestingEntry }[],
        investingActivitiesData: ICashFlowInvestingActivitiesComponentInput,
        investmentsKeyValue: string,
        disinvestmentsKeyValue: string,
    ): ICashFlowStatementInvestingEntry[] {
        if (!formValue) {
            formValue = [];
        }

        const investingActivitiesFlisDictionary = this.createFlisMap(investingActivitiesData?.flis ?? []);
        const updatedFromValue: ICashFlowStatementInvestingEntry[] = [];

        formValue.forEach((entry) => {
            const { parent } = entry;

            const matchingFli = investingActivitiesFlisDictionary.get(parent.fliId);
            if (matchingFli) {
                updatedFromValue.push(parent, ...entry.children);
            } else if (parent.isCustom) {
                updatedFromValue.push(parent, ...entry.children);
            }
        });

        const savedFlis = updatedFromValue.map((value) => value.fliId);

        const newFlis = investingActivitiesData.flis.filter((fli) => !savedFlis.includes(fli.id));

        const newFlisTableEntries = this.prepareTableEntriesForInvestingActivities(
            newFlis,
            investmentsKeyValue,
            disinvestmentsKeyValue,
        );

        updatedFromValue.push(...newFlisTableEntries);

        return updatedFromValue;
    }

    public static updateOperatingActivitiesFlisValues(
        formValue: ICashFlowStatementEntry[],
        operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput,
        savedActivitiesData: ICashFlowOperatingActivities,
    ): ICashFlowStatementEntry[] {
        if (!formValue) {
            formValue = [];
        }

        const operatingActivitiesFlisMap = this.createFlisMap(operatingActivitiesData?.flis ?? []);

        const updatedFormValue: ICashFlowStatementEntry[] = [];
        const existingFliIdsInForm = new Set<string>();

        // Update existing entries and filter out removed ones
        for (const entry of formValue) {
            if (entry.fliId === operatingActivitiesData.profitOfTheYearFli.name) {
                const updatedProfitFli = this.updateProfitFli(operatingActivitiesData, entry);
                updatedFormValue.push(updatedProfitFli);
                existingFliIdsInForm.add(entry.fliId);
                continue;
            }

            const updatedEntry = this.updateEntry(entry, operatingActivitiesFlisMap);
            if (updatedEntry) {
                updatedFormValue.push(updatedEntry);
                if (updatedEntry.fliId) {
                    existingFliIdsInForm.add(updatedEntry.fliId);
                }
            }
        }

        // Identify and add new FLIs that are not already in the form
        const newFlis: ICashFlowFLI[] = [];
        for (const [fliId, fli] of operatingActivitiesFlisMap.entries()) {
            if (!existingFliIdsInForm.has(fliId)) {
                newFlis.push(fli);
            }
        }

        if (newFlis.length > 0) {
            const newFlisTableEntries = this.prepareTableEntriesForOperatingActivities(newFlis, false);
            updatedFormValue.push(...newFlisTableEntries);
        }

        return updatedFormValue;
    }

    /**
     * Updates operating activities FLI values while preserving manual overrides (Shadow Copy Pattern).
     * 
     * This method:
     * 1. Updates system-calculated values from the mapping engine
     * 2. Preserves manually entered values (manuallyEnteredCurrentValue, manuallyEnteredPreviousValue)
     * 3. Does NOT overwrite manual values when mapping changes
     * 
     * The display logic in the component should show:
     * - Manual value if it exists
     * - System value otherwise
     */
    public static updateOperatingActivitiesFlisValuesWithShadowCopy(
        formValue: ICashFlowStatementEntry[],
        operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput,
        savedActivitiesData: ICashFlowOperatingActivities,
    ): ICashFlowStatementEntry[] {
        if (!formValue) {
            formValue = [];
        }

        const operatingActivitiesFlisMap = this.createFlisMap(operatingActivitiesData?.flis ?? []);

        const updatedFormValue: ICashFlowStatementEntry[] = [];
        const existingFliIdsInForm = new Set<string>();

        // Update existing entries and filter out removed ones
        for (const entry of formValue) {
            if (entry.fliId === operatingActivitiesData.profitOfTheYearFli.name) {
                const updatedProfitFli = this.updateProfitFliWithShadowCopy(operatingActivitiesData, entry);
                updatedFormValue.push(updatedProfitFli);
                existingFliIdsInForm.add(entry.fliId);
                continue;
            }

            const updatedEntry = this.updateEntryWithShadowCopy(entry, operatingActivitiesFlisMap);
            if (updatedEntry) {
                updatedFormValue.push(updatedEntry);
                if (updatedEntry.fliId) {
                    existingFliIdsInForm.add(updatedEntry.fliId);
                }
            }
        }

        // Identify and add new FLIs that are not already in the form
        const newFlis: ICashFlowFLI[] = [];
        for (const [fliId, fli] of operatingActivitiesFlisMap.entries()) {
            if (!existingFliIdsInForm.has(fliId)) {
                newFlis.push(fli);
            }
        }

        if (newFlis.length > 0) {
            const newFlisTableEntries = this.prepareTableEntriesForOperatingActivities(newFlis, false);
            updatedFormValue.push(...newFlisTableEntries);
        }

        return updatedFormValue;
    }

    /**
     * Update profit FLI while preserving manual overrides.
     */
    private static updateProfitFliWithShadowCopy(
        operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput,
        savedProfitFli: ICashFlowStatementEntry | undefined,
    ): ICashFlowStatementEntry {
        const profitFli = {} as ICashFlowStatementEntry;

        // Calculate new system value
        let profitCalculatedCurrentValue: number | null = null;
        if (NumberUtils.isFinite(operatingActivitiesData.currentAnnualProfit)) {
            profitCalculatedCurrentValue = NumberUtils.roundNumber(operatingActivitiesData.currentAnnualProfit);
        }

        let profitCalculatedPreviousValue: number | null = null;
        if (NumberUtils.isFinite(savedProfitFli?.calculatedPreviousValue)) {
            profitCalculatedPreviousValue = NumberUtils.roundNumber(savedProfitFli.calculatedPreviousValue);
        }

        // Set the system-calculated values
        profitFli.calculatedCurrentValue = profitCalculatedCurrentValue;
        profitFli.calculatedPreviousValue = profitCalculatedPreviousValue;
        profitFli.name = savedProfitFli?.name ?? operatingActivitiesData.profitOfTheYearFli.caption;
        profitFli.fliId = operatingActivitiesData.profitOfTheYearFli.name;

        // Preserve manual overrides if they exist
        if (savedProfitFli?.manuallyEnteredCurrentValue !== null && savedProfitFli?.manuallyEnteredCurrentValue !== undefined) {
            profitFli.manuallyEnteredCurrentValue = savedProfitFli.manuallyEnteredCurrentValue;
        }
        if (savedProfitFli?.manuallyEnteredPreviousValue !== null && savedProfitFli?.manuallyEnteredPreviousValue !== undefined) {
            profitFli.manuallyEnteredPreviousValue = savedProfitFli.manuallyEnteredPreviousValue;
        }

        return profitFli;
    }

    /**
     * Update a single entry while preserving manual overrides.
     * Only updates the calculatedCurrentValue/calculatedPreviousValue (system values).
     * Manual values (manuallyEnteredCurrentValue/manuallyEnteredPreviousValue) are preserved.
     */
    private static updateEntryWithShadowCopy(
        entry: ICashFlowStatementEntry,
        operatingActivitiesFlisMap: Map<string, ICashFlowFLI>,
    ): ICashFlowStatementEntry | null {
        const matchingFli = operatingActivitiesFlisMap.get(entry.fliId);

        // Remove non-custom entries that are no longer in the mapping
        if (!matchingFli && entry.fliId && !entry.isCustom) {
            return null;
        }

        // Update system values for existing, non-custom entries
        if (matchingFli && !entry.isCustom) {
            entry = this.updateEntrySystemValuesOnly(entry, matchingFli);
        }

        // Always preserve manual overrides (they're already on the entry)
        return entry;
    }

    /**
     * Updates only the system-calculated values on an entry, preserving manual overrides.
     */
    private static updateEntrySystemValuesOnly(
        entry: ICashFlowStatementEntry,
        matchingFli: ICashFlowFLI,
    ): ICashFlowStatementEntry {
        // Calculate new system value
        let newCalculatedCurrentValue: number | null = null;
        if (matchingFli.fliCashFlow === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
            newCalculatedCurrentValue = this.calculateCurrentNetworkingCapitalValue(matchingFli);
        } else {
            newCalculatedCurrentValue = this.getCalculatedCurrentValue(matchingFli);
        }

        // Update the system value (calculatedCurrentValue)
        // Note: The component will decide whether to display this or the manual override
        entry.calculatedCurrentValue = newCalculatedCurrentValue;
        entry.type = matchingFli.fliCashFlow;

        // Manual values are preserved as-is (they're already on the entry)
        return entry;
    }

    private static updateProfitFli(
        operatingActivitiesData: ICashFlowOperatingActivitiesComponentInput,
        savedProfitFli: ICashFlowStatementEntry | undefined,
    ): ICashFlowStatementEntry {
        let profitFli = {} as ICashFlowStatementEntry;
        let profitCalculatedCurrentValue: number | null = null;
        if (NumberUtils.isFinite(operatingActivitiesData.currentAnnualProfit)) {
            profitCalculatedCurrentValue = NumberUtils.roundNumber(operatingActivitiesData.currentAnnualProfit);
        }

        let profitCalculatedPreviousValue: number | null = null;
        if (NumberUtils.isFinite(savedProfitFli?.calculatedPreviousValue)) {
            profitCalculatedPreviousValue = NumberUtils.roundNumber(savedProfitFli.calculatedPreviousValue);
        }

        profitFli.calculatedCurrentValue = profitCalculatedCurrentValue;
        profitFli.name = savedProfitFli?.name ?? operatingActivitiesData.profitOfTheYearFli.caption;
        profitFli.fliId = operatingActivitiesData.profitOfTheYearFli.name;
        profitFli.calculatedPreviousValue = profitCalculatedPreviousValue;

        return profitFli;
    }

    private static createFlisMap(flis: ICashFlowFLI[]): Map<string, ICashFlowFLI> {
        return new Map(flis.map((fli) => [fli.id, fli]));
    }

    private static createFilteredSavedEntriesMap(
        savedActivitiesData: ICashFlowOperatingActivities,
        currentActivitiesFlisMap: Map<string, ICashFlowFLI>,
        profitOfTheYearFli: string,
    ): Map<string, ICashFlowStatementEntry> {
        return new Map(
            savedActivitiesData?.entries
                ?.filter(
                    (entry) =>
                        currentActivitiesFlisMap.has(entry.fliId) ||
                        entry.isCustom ||
                        entry.fliId === profitOfTheYearFli,
                )
                .map((entry) => [entry.fliId, entry]) ?? [],
        );
    }

    private static updateEntry(
        entry: ICashFlowStatementEntry,
        operatingActivitiesFlisMap: Map<string, ICashFlowFLI>,
    ): ICashFlowStatementEntry | null {
        const matchingFli = operatingActivitiesFlisMap.get(entry.fliId);

        if (!matchingFli && entry.fliId && !entry.isCustom) {
            return null;
        }

        if (matchingFli && !entry.isCustom) {
            entry = this.updateEntryWithMatchingFli(entry, matchingFli);
        }

        return entry;
    }

    private static updateEntryWithMatchingFli(
        entry: ICashFlowStatementEntry,
        matchingFli: ICashFlowFLI,
    ): ICashFlowStatementEntry {
        if (matchingFli.fliCashFlow === FinancialLineItemCashFlowType.NETWORKING_CAPITAL) {
            entry.calculatedCurrentValue = this.calculateCurrentNetworkingCapitalValue(matchingFli);
        } else {
            entry.calculatedCurrentValue = this.getCalculatedCurrentValue(matchingFli);
        }
        entry.type = matchingFli.fliCashFlow;

        return entry;
    }

    private static getCalculatedCurrentValue(fli: ICashFlowFLI): number | null {
        if (fli.currentValue != null && NumberUtils.isFinite(fli.currentValue)) {
            return NumberUtils.multiplyNumbers([fli.currentValue, -1]);
        }
        return null;
    }

    private static getNewFlis(
        operatingActivitiesFlisMap: Map<string, ICashFlowFLI>,
        savedEntriesMap: Map<string, ICashFlowStatementEntry>,
    ): ICashFlowFLI[] {
        return Array.from(operatingActivitiesFlisMap.keys())
            .filter((key) => !savedEntriesMap.has(key))
            .map((key) => operatingActivitiesFlisMap.get(key)!);
    }

    private static calculateCurrentNetworkingCapitalValue(cashFlowFli: ICashFlowFLI): number | null {
        if (
            (cashFlowFli.currentValue == null || !NumberUtils.isFinite(cashFlowFli.currentValue)) &&
            (cashFlowFli.openingValue == null || !NumberUtils.isFinite(cashFlowFli.openingValue))
        ) {
            return null;
        }

        if (cashFlowFli.fliType === FinancialLineItemType.ASSETS) {
            return NumberUtils.subtractNumbers(cashFlowFli.openingValue ?? 0, cashFlowFli.currentValue ?? 0);
        } else if (cashFlowFli.fliType === FinancialLineItemType.LIABILITIES) {
            return NumberUtils.subtractNumbers(cashFlowFli.currentValue ?? 0, cashFlowFli.openingValue ?? 0);
        }

        return null;
    }

    public static calculateTableEntriesTotals(tableEntries: ICashFlowStatementEntry[]): {
        currentTotal: number;
        previousTotal: number;
    } {
        const currentValueEntries = tableEntries.map<number | null>((entry) => entry.manuallyEnteredCurrentValue != null ? entry.manuallyEnteredCurrentValue :
            entry.calculatedCurrentValue);
        const currentTotal = NumberUtils.sumIgnoringInfiniteNumbers(currentValueEntries, 0);

        const previousValueEntries = tableEntries.map((entry) => entry.manuallyEnteredPreviousValue != null ? entry.manuallyEnteredPreviousValue :
            entry.calculatedPreviousValue);
        const previousTotal = NumberUtils.sumIgnoringInfiniteNumbers(previousValueEntries, 0);

        return {
            currentTotal,
            previousTotal,
        };
    }

    public static mapStringValuesInOperatingAndFinancingTablesToNumbers(
        dataSource: ICashFlowStatementEntry[],
    ): ICashFlowStatementEntry[] {
        for (const entry of dataSource) {
            let calculatedCurrentValue: number | null = null;
            if (entry.calculatedCurrentValue != null && NumberUtils.isFinite(entry.calculatedCurrentValue)) {
                calculatedCurrentValue = NumberUtils.roundNumber(entry.calculatedCurrentValue);
            }

            let calculatedPreviousValue: number | null = null;
            if (entry.calculatedPreviousValue != null && NumberUtils.isFinite(entry.calculatedPreviousValue)) {
                calculatedPreviousValue = NumberUtils.roundNumber(entry.calculatedPreviousValue);
            }
            entry.calculatedCurrentValue = calculatedCurrentValue;
            entry.calculatedPreviousValue = calculatedPreviousValue;

            // Also process manual override values for saving
            if (entry.manuallyEnteredCurrentValue != null && NumberUtils.isFinite(entry.manuallyEnteredCurrentValue)) {
                entry.manuallyEnteredCurrentValue = NumberUtils.roundNumber(entry.manuallyEnteredCurrentValue);
            }
            if (entry.manuallyEnteredPreviousValue != null && NumberUtils.isFinite(entry.manuallyEnteredPreviousValue)) {
                entry.manuallyEnteredPreviousValue = NumberUtils.roundNumber(entry.manuallyEnteredPreviousValue);
            }
        }
        return dataSource;
    }

    public static mapStringValuesInInvestingTableToNumbers(
        dataSource: ICashFlowStatementInvestingEntry[],
    ): ICashFlowStatementInvestingEntry[] {
        for (const entry of dataSource) {
            let calculatedCurrentValue: number | null = null;
            if (entry.calculatedCurrentValue != null && NumberUtils.isFinite(entry.calculatedCurrentValue)) {
                calculatedCurrentValue = NumberUtils.roundNumber(entry.calculatedCurrentValue);
            }

            let calculatedPreviousValue: number | null = null;
            if (entry.calculatedPreviousValue != null && NumberUtils.isFinite(entry.calculatedPreviousValue)) {
                calculatedPreviousValue = NumberUtils.roundNumber(entry.calculatedPreviousValue);
            }
            entry.calculatedCurrentValue = calculatedCurrentValue;
            entry.calculatedPreviousValue = calculatedPreviousValue;
        }
        return dataSource;
    }
}
